import {
  colors,
  customEvent,
  getCdnImageSrc,
  useAlertDialog,
  VStack,
} from "goi_common";
import { useRouter } from "next/router";
import { useState } from "react";
import { useOverlay } from "@toss/use-overlay";

import { OverlayModal } from "goi_common/src/components/OverlayModal";
import {
  EmptyAccountConfirmContent,
  hasVisibleBankAccount,
  isEmptyAccountConfirmDismissed,
  setEmptyAccountConfirmDismissed,
} from "@/components/Modals/ShareChoiceWithAccountGuard";
import { FixedButton } from "@/components/FixedButton";
import { MOBILE_WIDTH } from "@/constants/layout";
import { useGetObituary } from "@/containers/BugoContainer/BugoCreateContainer/hooks/useGetObituary";
import { getIsFlowerOrderEnabled } from "@/containers/BugoContainer/utils";

import { getObituarySendMessageTemplate } from "@/hooks/obituary/useObituaryNativeShare";
import { BugoNativeShareChoiceSelfItem } from "@/containers/BugoShareContainer/components/BugoNativeShareChoiceSelfItem";
import { AuthProvider } from "@/components/AuthProvider";

const BugoShareChoiceSelf = () => {
  return (
    <AuthProvider>
      <BugoShareChoiceSelfContainer title="누구의 이름으로 발송하시겠어요?" />
    </AuthProvider>
  );
};

export default BugoShareChoiceSelf;

interface BugoShareChoiceSelfContainerProps {
  title?: string;
  description?: string;
}

export type BugoChoiceSelfShareType =
  | "message"
  | "contact"
  | "copyLink"
  | "kakao";

export const BUGO_CHOICE_SHARE_TYPE: Record<string, BugoChoiceSelfShareType> = {
  message: "message",
  contact: "contact",
  copyLink: "copyLink",
  kakao: "kakao",
};

export const BugoShareChoiceSelfContainer = ({
  title,
  description,
}: BugoShareChoiceSelfContainerProps) => {
  const router = useRouter();
  const { obituaryKey, shareType } = router.query as {
    obituaryKey?: string;
    shareType: BugoChoiceSelfShareType;
  };

  const { data: obituary } = useGetObituary();
  // URL 쿼리는 화면 초기화 중 비어 있을 수 있으므로, 조회된 부고의 정규 키를 우선 쓴다.
  const dismissalObituaryKey = obituary?.key ?? obituaryKey;

  const [selectedSangjuKey, setSelectedSangjuKey] = useState<string>("");
  const selectedSangju = obituary?.sangjus?.find(
    (sangju) => sangju.key === selectedSangjuKey
  );

  const { openAlertDialog } = useAlertDialog();
  const overlay = useOverlay();

  /**
   * 선택한 상주의 링크에 노출될 계좌가 하나도 없으면 공유 직전에 한 번 확인한다.
   * 상주마다 공유 범위 설정이 달라, 같은 부고여도 고른 상주에 따라 결과가 달라진다.
   */
  const onClickShare = () => {
    if (!selectedSangju) {
      return openAlertDialog({
        content: "상주를 선택해주세요",
        buttonText: "확인",
      });
    }

    if (hasVisibleBankAccount(obituary?.sangjus ?? [], selectedSangju)) {
      return shareObituary();
    }

    if (isEmptyAccountConfirmDismissed(dismissalObituaryKey)) {
      return shareObituary();
    }

    overlay.open(({ isOpen, close: onClose }) => (
      <OverlayModal
        closeOnOverlayClick
        isOpen={isOpen}
        onClose={onClose}
        modalContentStyle={{
          maxWidth: "300px",
          borderRadius: "20px",
        }}
        overlayModalContent={
          <EmptyAccountConfirmContent
            onClickAccountSetting={() => {
              onClose();
              router.push(
                `/bugo/share/invite-sangju?obituaryKey=${obituaryKey}`
              );
            }}
            onConfirm={(doNotShowAgain) => {
              if (doNotShowAgain)
                setEmptyAccountConfirmDismissed(dismissalObituaryKey, true);
              onClose();
              shareObituary();
            }}
          />
        }
      />
    ));
  };

  const shareObituary = () => {
    if (!selectedSangju) {
      return openAlertDialog({
        content: "상주를 선택해주세요",
        buttonText: "확인",
      });
    }

    const 부고정보 = {
      고인이름: obituary?.name,
      관계: selectedSangju?.relation,
      상주이름: selectedSangju?.name,
      날짜: obituary?.passed_away_on || "",
      발인: obituary?.leave_parlor_at || "",
      장례식장: obituary?.parlor?.companyname || "",
      빈소: obituary?.binso,
      사망표현: obituary?.die_term,
    };

    if (shareType === BUGO_CHOICE_SHARE_TYPE.message) {
      const shareMessageTemplate = () => {
        const shareURL = `${window.location.origin}/share/visitor/${selectedSangjuKey}/`;

        const message = encodeURIComponent(
          getObituarySendMessageTemplate(부고정보)
        );

        // /** android */
        // ("sms:?body=/* body text here */");
        // /** ios8 미만 */
        // ("sms:body=/* body text here */");
        // /** ios8 이상 */
        // ("sms:&body=/* body text here */");
        router.push(`sms:?body=${shareURL + "\n" + message}`);
      };

      shareMessageTemplate();
      return;
    }

    if (shareType === BUGO_CHOICE_SHARE_TYPE.copyLink) {
      const copyClipboardShareTemplate = () => {
        const shareURL = `${window.location.origin}/share/visitor/${selectedSangjuKey}/`;

        const message = getObituarySendMessageTemplate(부고정보);

        navigator.clipboard.writeText(shareURL + "\n\n" + message).then(() => {
          openAlertDialog({
            content: "복사되었습니다.",
            buttonText: "확인",
          });
        });
      };

      copyClipboardShareTemplate();
      return;
    }

    if (shareType === BUGO_CHOICE_SHARE_TYPE.contact) {
      router.push(
        `/contact/share?obituaryKey=${obituaryKey}&sangjuKey=${selectedSangjuKey}`
      );
    }

    if (shareType === BUGO_CHOICE_SHARE_TYPE.kakao) {
      const visitorUrl = `${window.location.origin}/share/visitor/${selectedSangjuKey}?share=kakao`;
      const flowerUrl = `${window.location.origin}/flower/select?sangjuKey=${selectedSangjuKey}&share=kakao`;

      const buttons: {
        title: string;
        link: { webUrl: string; mobileWebUrl: string };
      }[] = [
        {
          title: "부고장 확인하기",
          link: { webUrl: visitorUrl, mobileWebUrl: visitorUrl },
        },
      ];
      if (getIsFlowerOrderEnabled(obituary)) {
        buttons.push({
          title: "근조화환 보내기",
          link: { webUrl: flowerUrl, mobileWebUrl: flowerUrl },
        });
      }

      const shareContent = {
        objectType: "feed",
        content: {
          title: "부고 알림",
          description: getObituarySendMessageTemplate(부고정보),
          imageUrl: getCdnImageSrc("bugo/bugo_kakao_share.png"),
          imageWidth: 800,
          imageHeight: 400,
          link: {
            webUrl: visitorUrl,
            mobileWebUrl: visitorUrl,
          },
        },
        buttons,
      };

      return window.Kakao?.Share?.sendDefault(shareContent);
    }
  };

  return (
    <VStack width="100%" alignItems="center">
      <VStack
        width="100%"
        css={{
          maxWidth: `${MOBILE_WIDTH}px`,
          minHeight: "calc(100dvh - 66px)",
          background: colors.warmGray50,
        }}
      >
        <VStack
          css={{
            padding: "58px 24px 27px 24px",
          }}
        >
          <h2 className="subtitle1_sb">{title}</h2>
          <div>
            <p
              className="body1 gray500"
              css={{
                whiteSpace: "pre-wrap",
              }}
            >
              {description}
            </p>
          </div>
        </VStack>
        <VStack
          css={{
            width: "100%",
          }}
        >
          {obituary?.sangjus &&
            obituary?.sangjus.length > 0 &&
            obituary?.sangjus.map(
              ({ key: sangjuKey, relation, name }, index) => (
                <BugoNativeShareChoiceSelfItem
                  key={sangjuKey}
                  relation={relation}
                  name={name}
                  isChecked={sangjuKey === selectedSangjuKey}
                  index={index}
                  onChange={() => {
                    customEvent({
                      path: "bugo_share_choice",
                      trigger_and_target: "btn_select_sangju",
                    });
                    setSelectedSangjuKey(sangjuKey || "");
                  }}
                />
              )
            )}
        </VStack>
      </VStack>
      <FixedButton
        onClick={() => {
          customEvent({
            path: "bugo_share_choice",
            trigger_and_target: "btn_submit-share",
          });
          onClickShare();
        }}
      >
        {shareType === "copyLink" ? "복사하기" : "발송하기"}
      </FixedButton>
    </VStack>
  );
};
